<template>
  <div class="suggest-analysis">
    <div class="suggest-analysis-title mb-3 d-flex justify-content-between">
      <span class="suggest-analysis-title-text" @click="handleClick">{{ $t('meter.suggest_analysis') }}</span>
      <div class="bar ml-2 d-flex">
        <fixed-label-filter :label="$t('bill.group')">
          <base-select v-model="reportType" :options="reportTypeOpts" minWidth="120px" width="120px" />
        </fixed-label-filter>
        <fixed-label-filter class="ml-2" :label="$t('common_249')">
          <base-select v-model="valueType" :options="valueTypeOpts" minWidth="120px" width="120px" />
        </fixed-label-filter>
        <fixed-label-filter class="ml-2" label="TOP">
          <base-select v-model="top" :options="topOptions" minWidth="60px" width="60px" />
        </fixed-label-filter>
      </div>
    </div>
    <div class="suggest-analysis-content">
      <div class="d-flex" style="min-height: 400px;height: 400px">
        <div class="left" :style="isTemplate ? 'flex: 0 0 40%' : ''">
          <scrollbar v-if="list.length > 0" tag="ul" class="list-wrapper mt-2" style="height: 100%">
            <li :class="`list-item d-flex ${hiddenList.includes(item.name) ? 'opacity80' : ''}`" v-for="(item,index) in list" :key="index">
              <div class="item-side">
                <div class="circle mr-2" :style="{'backgroundColor': hiddenList.includes(item.name) ? '#ccc' : item.color}" />
                <div class="name">{{ item.name }}</div>
              </div>
              <div class="item-side" style="flex-shrink:0">
                <div class="value"><list-body-cell-wrap copy :row="{value: valueShowType === 'resource_count' ? item.value : numerifyValue(item.value) }" field="value" :message="item.value" /> </div>
                <div class="percent ml-2" style="flex-shrink:0">{{ percent(item.value) }}</div>
              </div>
            </li>
          </scrollbar>
          <a-alert v-else :message="$t('bill.text_85')" banner />
        </div>
        <div class="right" :style="isTemplate ? 'flex: 0 0 60%' : ''">
          <e-chart id="suggest-analysis-chart" domId="suggest-analysis-chart" :options="chartOptions" chart-style="width:100%;height:100%" autoresize @chartInstance="chartCreate" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { numerify } from '@/filters'
import { chartColors } from '@/constants'
import { getBrandName } from '@/utils/common/hypervisor'

export default {
  name: 'SuggestAnalysis',
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    timeParams: {
      type: Object,
      required: true,
    },
    isTemplate: {
      type: Boolean,
      default: false,
    },
    templateParams: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      reportTypeOpts: [
        { id: 'domain', label: this.$t('dictionary.domain') },
        { id: 'project', label: this.$t('dictionary.project') },
        { id: 'brand', label: this.$t('bill.text_81') },
        { id: 'account', label: this.$t('bill.text_60') },
      ],
      valueTypeOpts: [
        { id: 'alert_count', label: this.$t('subscription.alert_count') },
        { id: 'resource_count', label: this.$t('subscription.resource_count') },
        { id: 'effect_count', label: this.$t('subscription.effect_count') },
        { id: 'effect_resource_count', label: this.$t('subscription.effect_resource_count') },
      ],
      reportType: this.templateParams.reportType || 'brand',
      valueType: this.templateParams.valueType || 'alert_count',
      top: this.templateParams.top || 5,
      topOptions: [
        { id: 5, name: '5' },
        { id: 10, name: '10' },
        { id: 15, name: '15' },
        { id: 20, name: '20' },
      ],
      hiddenList: [],
    }
  },
  computed: {
    colors () {
      return this._dataList.map((item, index) => item.color)
    },
    totalMoney () {
      const target = this.data[this.reportType] || []
      return target.map(item => {
        return {
          value: item[this.valueType],
        }
      }).reduce((acc, curr) => acc + curr.value, 0)
    },
    _dataList () {
      const target = this.data[this.reportType] || []
      target.sort((a, b) => a[this.valueType] - b[this.valueType])
      const first = (target.length > this.top ? target.slice(0, this.top) : target).map((item, index) => {
        return {
          name: this.reportType === 'brand' ? getBrandName(item.provider) : item[this.reportType],
          value: item[this.valueType],
          color: chartColors[index % chartColors.length],
        }
      })
      const last = (target.length > this.top ? target.slice(this.top) : []).map(item => {
        return {
          value: item[this.valueType],
        }
      })
      const rest = last.length ? [{
        type: 'other',
        name: this.$t('common.text00053'),
        value: last.reduce((acc, curr) => acc + curr.value, 0),
      }] : []
      return [...first, ...rest].map((item, index) => {
        return {
          ...item,
          color: item.type === 'other' ? '#ccc' : chartColors[index % chartColors.length],
        }
      })
    },
    list () {
      const list = [...this._dataList]
      list.reverse()
      return list
    },
    chartOptions () {
      const idx = this._dataList.findIndex(item => item.type === 'other')
      const series = this._dataList.map((item, index) => {
        const ret = {
          name: item.name,
          type: 'bar',
          barWidth: 30,
          stack: 'first',
          data: new Array(this._dataList.length).fill(0),
        }
        ret.data[index] = item.value
        return ret
      })
      if (idx !== -1) {
        series.map((item, index) => {
          item.data[idx] = { value: item.data[idx], itemStyle: { color: '#ccc' } }
        })
      }
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          position: (point, params, dom, rect, size) => {
            const list = params.filter(item => item.name === item.seriesName)
            dom.style.border = 'none'
            dom.style.backgroundColor = 'transparent'
            if (list.length) {
              const wrapper = `<div class="chart-tooltip-wrapper">
                                <div style="color: #5D6F80;">${list[0].marker} ${list[0].name}: ${list[0].value}</div>
                              </div>`
              dom.innerHTML = wrapper
            } else {
              const wrapper = `<div class="chart-tooltip-wrapper">
                              </div>`
              dom.innerHTML = wrapper
            }
          },
        },
        legend: {
          show: true,
          bottom: 0,
          type: 'scroll',
          width: '95%',
        },
        color: this.colors,
        grid: {
          left: '3%',
          right: '4%',
          bottom: '10%',
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          boundaryGap: [0, 0.01],
        },
        yAxis: {
          type: 'category',
          data: this._dataList.map(item => item.name),
        },
        series,
      }
    },
  },
  watch: {
    data: {
      handler () {
        this.hiddenList = []
      },
      deep: true,
    },
  },
  methods: {
    handleClick () {
      this.$router.push({
        path: '/suggestsysreport',
        query: {
          report_type: 'provider-report',
        },
      })
    },
    numerifyValue (num) {
      return numerify(num, '0,0')
    },
    percent (num) {
      if (!this.totalMoney) return '0%'
      if (Number(num) === 0) return '0%'
      if (Number(num) < 0) return '-'
      const float = num / this.totalMoney
      if (float < 0.0001) {
        return '< 0.01%'
      }
      return `${numerify(float * 100, '0')}%`
    },
    chartCreate (chart) {
      const that = this
      chart.on('legendselectchanged', function (params) {
        const { selected = {} } = params
        const hiddenList = Object.keys(selected).filter(item => !selected[item])
        that.hiddenList = hiddenList
      })
    },
    getTemplateParams () {
      return {
        reportType: this.reportType,
        valueType: this.valueType,
        top: this.top,
      }
    },
  },
}
</script>

<style lang="less" scoped>
.suggest-analysis {
  border: 1px solid #e8e8e8;
  padding: 12px;
  border-radius: 3px;
  margin-top: 12px;
  .suggest-analysis-title {
    height: 30px;
  }
  .list-wrapper {
    :global(.el-scrollbar__wrap) {
      overflow-x: hidden;
    }
    ::v-deep ul {
      list-style: none;
      padding-left: 0;
    }
    ::v-deep .is-horizontal {
      display: none;
    }
    ::v-deep .scrollbar-wrap {
      overflow-x: hidden;
    }
    .list-item {
      line-height: 40px;
      height: 40px;
      border-bottom: 1px solid #dce0e6;
      font-size: 12px;
      justify-content: space-between;
      flex-wrap: nowrap;
      .item-side {
        display: flex;
        flex-wrap: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        word-break: break-all;
        white-space: nowrap;
      }
      .name {
        color: #000;
        overflow: hidden;
        text-overflow: ellipsis;
        word-break: break-all;
        white-space: nowrap;
      }
      .value {
        color: #939eab;
        display: flex;
      }
      .percent {
        border: 1px solid #4da1ff;
        color: #4da1ff;
        height: 20px;
        line-height: 20px;
        margin-top: 10px;
        padding: 0 6px;
      }
      .circle {
        margin-top: 14px;
        height: 14px;
        width: 14px;
        border-radius: 50%;
        flex: 0 0 14px;
        // border: 1px solid #ccc;
      }
    }
    .opacity80 {
      opacity: 0.9;
      .name {
        color: #ccc;
      }
      .value {
        color: #ccc;
      }
      .percent {
        border: 1px solid #ccc;
        color: #ccc;
      }
    }
  }
}
.left {
  flex: 0 0 50%;
  height: 100%;
}
.right {
  width: 50%;
  flex: 1 1 auto;
  height: 100%;
}
.opacity80 {
  opacity: 0.9;
  .name {
    color: #ccc;
  }
  .value {
    color: #ccc;
  }
  .percent {
    border: 1px solid #ccc;
    color: #ccc;
  }
}
.suggest-analysis-title-text {
  &:hover {
    cursor: pointer;
    color: var(--antd-wave-shadow-color);
  }
}
</style>
